import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Injector,
    OnDestroy,
    OnInit,
    QueryList,
    Renderer2,
    ViewChild,
    ViewChildren,
    ViewEncapsulation,
    computed,
    effect,
    inject,
    viewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatDialogRef } from '@angular/material/dialog';
import { FiImageSize, FileType } from '@fi-shared/types';
import { FI_DIALOGS_CONTENT_DATA, FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { FiDialogsContainerBase } from '@fi-shared/fi-dialogs/fi-dialogs-container-base';
import { isDefined } from '@fi-shared/utils';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';
import { IMediaBankFile, IMediaBankFileReference } from '@fi-shared/media-bank';
import { IImagePreviewData } from '@fi-shared/media-bank/media-bank';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiBrowserEventsService } from '@fi-shared/fi-shared-services/fi-browser-events.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { LEFT_ARROW, RIGHT_ARROW } from '@angular/cdk/keycodes';
import { MediaBankStorageType } from '@fi-shared/media-bank/media-bank-storage-type';
import { MobileAppHelperBaseService } from '@fi-shared/fi-shared-services/mobile-app-helper-base.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { IVideoProgressEvent } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo-player-api';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import ColorThief from 'colorthief/dist/color-thief';

interface IDominantColorItem {
    imageIndex: number;
    rgbValue: number[];
}

@Component({
    selector: 'fi-media-viewer',
    templateUrl: './media-viewer.component.html',
    styleUrls: ['./media-viewer.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-media-viewer' },
    standalone: false,
})
export class MediaViewerComponent
    extends FiDialogsContentBase<IImagePreviewData, boolean>
    implements OnInit, OnDestroy
{
    private mediaBankHelperService = inject(MediaBankHelperService);
    private fiMediaStorageService = inject(FiMediaStorageService);
    private detectDevicesService = inject(DetectDevicesService);
    private fiBrowserEventsService = inject(FiBrowserEventsService);
    private renderer = inject(Renderer2);
    private cdr = inject(ChangeDetectorRef);
    private mobileAppHelperBaseService = inject(MobileAppHelperBaseService);
    private statisticService = inject(StatisticService);

    @ViewChild('dominantColorOverlay') dominantColorOverlayElementRef: ElementRef<HTMLDivElement>;
    @ViewChildren('imageContainer') imageContainers: QueryList<ElementRef>;
    private safeUrl: SafeResourceUrl;
    private colorThief = new ColorThief();
    private readonly backgroundOverlayOpacity: number = 0.97;
    private dominantColors: IDominantColorItem[] = [];
    protected mediaBankFileReferences: IMediaBankFileReference[] = [];
    private currentMediaBankFileIndex: number;
    protected readonly FileType = FileType;

    protected readonly listenToVideoProgress: boolean = isDefined(this.data.videoProgressListener$);
    private regularVideoElementRef = viewChild<ElementRef<HTMLVideoElement>>('regularVideoElement');
    private regularVideoElement = computed<HTMLVideoElement | undefined>(() => {
        return this.regularVideoElementRef()?.nativeElement;
    });
    private _regularVideoProgressListenerEffect = effect(() => {
        const videoEl = this.regularVideoElement();
        if (!isDefined(videoEl) || !this.listenToVideoProgress) {
            return;
        }
        const timeupdateHandler = () => {
            const progress = (videoEl.currentTime / videoEl.duration) * 100;
            this.handleVideoProgress({
                seconds: videoEl.currentTime,
                duration: videoEl.duration,
                percent: progress,
            });
        };
        const endedHandler = () => {
            this.handleVideoProgress({
                seconds: videoEl.currentTime,
                duration: videoEl.duration,
                percent: 100,
            });
        };
        videoEl.addEventListener('timeupdate', timeupdateHandler);
        videoEl.addEventListener('ended', endedHandler);
        return () => {
            videoEl?.removeEventListener('timeupdate', timeupdateHandler);
            videoEl?.removeEventListener('ended', endedHandler);
        };
    });

    constructor(
        private sanitizer: DomSanitizer,
        elementRef: ElementRef,
        dialogRef: MatDialogRef<FiDialogsContainerBase<any, any>>,
        injector: Injector,
        @Inject(FI_DIALOGS_CONTENT_DATA) public data: IImagePreviewData,
    ) {
        super(dialogRef, data, injector);
        if (this.isCurrentFileType(FileType.Pdf)) {
            setTimeout(() => {
                const iframe = elementRef.nativeElement.querySelector('iframe');
                iframe.style.height = Math.round(window.innerHeight * 0.95) + 'px';
                iframe.style.width = Math.round(window.innerWidth * 0.95) + 'px';
            });
        }
    }

    public ngOnInit(): void {
        this.setAvailableMediaBankFileReferences();
        this.handleArrowKeysPress();

        if (!this.data.skipStatisticsSave) {
            this.statisticService.saveFilesViewAction(
                this.mediaBankFileReferences.filter((item) => item.MediaBankFileId).map((item) => item.MediaBankFileId),
            );
        }
    }

    protected handleVideoProgress(event: IVideoProgressEvent): void {
        this.data.videoProgressListener$?.next(event);
    }

    private getLeftOffsetByIndex(index: number): number {
        if (index < this.currentMediaBankFileIndex) {
            return -((this.currentMediaBankFileIndex - index) * 100);
        }
        if (index > this.currentMediaBankFileIndex) {
            return (index - this.currentMediaBankFileIndex) * 100;
        }
        return 0;
    }

    public setDominantColor(img: HTMLImageElement, imageIndex: number): void {
        let rgbValue: number[];
        try {
            rgbValue = this.colorThief.getColor(img);
        } catch (ex) {
            // cors errors for upcloud images
            // https://chainformation.atlassian.net/browse/FI-6950
            rgbValue = [255, 255, 255];
        }
        this.dominantColors.push({
            imageIndex,
            rgbValue: rgbValue,
        });
        if (imageIndex === this.currentMediaBankFileIndex) {
            this.setCurrentDominantColor();
        }
    }

    protected isMobilePhone: boolean = this.detectDevicesService.isMobilePhone();
    protected isMobileAppIos: boolean = this.mobileAppHelperBaseService.isMobileAppIos();

    protected getImageUrl(mediaBankFileReference: IMediaBankFileReference): string {
        const imageUrl = this.fiMediaStorageService.getMediaFileUrl(mediaBankFileReference, {
            size: FiImageSize.Large,
            contentDisposition: 'inline',
            alwaysUseDownloadFromObjectStorageUrl: mediaBankFileReference.FileType === FileType.Image,
        });
        return imageUrl;
    }

    protected isCurrentMediaBank(index: number): boolean {
        return this.currentMediaBankFileIndex === index;
    }

    protected isDownloadButtonShown(): boolean {
        return this.isCurrentFileType(FileType.Image) && !this.isImgByUrl();
    }

    protected isCurrentFileType(fileType: FileType): boolean {
        return this.data.fileType === fileType;
    }

    protected onCloseClick($event?: Event): void {
        if (isDefined($event) && ($event.target as HTMLElement).classList.contains('main-image')) {
            return;
        }
        this.dialogRef.close();
    }

    protected downloadImage(): void {
        if (this.isCurrentFileType(FileType.Image)) {
            this.mediaBankHelperService.downloadMediaBankFileRef(
                this.mediaBankFileReferences[this.currentMediaBankFileIndex],
            );
        } else if (isDefined(this.data.mediaBankFileReferences[0])) {
            this.mediaBankHelperService.downloadMediaBankFileRef(this.data.mediaBankFileReferences[0]);
        } else {
            this.mediaBankHelperService.downloadMediaBankFile(this.data.mediaBankFiles[0]);
        }
    }

    protected getSafeUrl(): SafeResourceUrl {
        if (!this.safeUrl) {
            this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.data.url);
        }
        return this.safeUrl;
    }

    protected isFirstMediaBank(): boolean {
        return this.currentMediaBankFileIndex === 0;
    }

    protected isSingleMediaBank(): boolean {
        return this.mediaBankFileReferences.length === 1;
    }

    protected isLastMediaBank(): boolean {
        return this.currentMediaBankFileIndex + 1 === this.mediaBankFileReferences.length;
    }

    protected showPreviousImage(): void {
        if (!this.isFirstMediaBank()) {
            this.currentMediaBankFileIndex--;
            this.setCurrentDominantColor();
            this.setImageContainersOffset();
        }
    }

    protected showNextImage(): void {
        if (!this.isLastMediaBank()) {
            this.currentMediaBankFileIndex++;
            this.setCurrentDominantColor();
            this.setImageContainersOffset();
        }
    }

    protected isFooterNavigationShown(): boolean {
        return !this.isMobilePhone && this.mediaBankFileReferences.length > 1;
    }

    protected isImgByUrl(): boolean {
        return !this.mediaBankFileReferences.length && isDefined(this.data.url);
    }

    private setImageContainersOffset() {
        this.imageContainers.forEach((imageContainer: ElementRef, index: number) => {
            this.renderer.setStyle(imageContainer.nativeElement, 'left', `${this.getLeftOffsetByIndex(index)}%`);
        });
    }

    private setCurrentDominantColor() {
        this.renderer.setStyle(
            this.dominantColorOverlayElementRef.nativeElement,
            'backgroundColor',
            this.getCurrentDominantColor(),
        );
    }

    private getCurrentDominantColor(): string {
        const dominantColor: IDominantColorItem = this.dominantColors.find((dominantColor: IDominantColorItem) => {
            return dominantColor.imageIndex === this.currentMediaBankFileIndex;
        });
        return !isDefined(dominantColor)
            ? ''
            : `rgba(${dominantColor.rgbValue.join(', ')}, ${this.backgroundOverlayOpacity})`;
    }

    private handleArrowKeysPress() {
        this.fiBrowserEventsService
            .getDocumentEventSubject$('keyup')
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(($event: KeyboardEvent) => {
                if ($event.keyCode === LEFT_ARROW) {
                    this.showPreviousImage();
                    this.cdr.detectChanges();
                    return;
                }
                if ($event.keyCode === RIGHT_ARROW) {
                    this.showNextImage();
                    this.cdr.detectChanges();
                    return;
                }
            });
    }

    private setAvailableMediaBankFileReferences() {
        this.data.mediaBankFileReferences = isDefined(this.data.mediaBankFileReferences)
            ? this.data.mediaBankFileReferences.filter((mediaBankFileReference: IMediaBankFileReference) => {
                  return isDefined(mediaBankFileReference);
              })
            : [];

        this.data.mediaBankFiles = isDefined(this.data.mediaBankFiles)
            ? this.data.mediaBankFiles.filter((mediaBankFile: IMediaBankFile) => isDefined(mediaBankFile))
            : [];

        this.mediaBankFileReferences = this.data.mediaBankFileReferences.length
            ? this.data.mediaBankFileReferences
            : this.data.mediaBankFiles.map((mediaBankFile: IMediaBankFile) => {
                  return this.mediaBankHelperService.getMediaBankFileReferenceFromMediaFile(mediaBankFile, {}); // todo why we need here map files to references?
              });

        // todo: remove this filter when new layout is used for pdf preview
        if (this.mediaBankFileReferences.length) {
            this.mediaBankFileReferences = this.mediaBankFileReferences.filter(
                (mediaBankFileReference: IMediaBankFileReference) => {
                    return this.isImageMediaBank(mediaBankFileReference) || this.isVimeoVideo(mediaBankFileReference);
                },
            );
        }

        this.currentMediaBankFileIndex = this.mediaBankFileReferences.findIndex(
            (mediaBankFileReference: IMediaBankFileReference) => {
                const mediaBankUrl: string = this.getImageUrl(mediaBankFileReference);
                return this.data.url && (mediaBankUrl === this.data.url || this.data.url.includes(mediaBankUrl));
            },
        );

        if (this.currentMediaBankFileIndex === -1) {
            this.currentMediaBankFileIndex = 0;
        }

        setTimeout(() => {
            this.setImageContainersOffset();
        });
    }

    protected isImageMediaBank(mediaBankFileReference: IMediaBankFileReference): boolean {
        return mediaBankFileReference.FileType === FileType.Image;
    }

    protected isVimeoVideo(mediaBankFileReference: IMediaBankFileReference): boolean {
        return (
            (mediaBankFileReference.FileType === FileType.Movie ||
                mediaBankFileReference.FileType === FileType.MovieNotPlayable) &&
            mediaBankFileReference.StorageType === MediaBankStorageType.Vimeo
        );
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
